import { Elements, PaymentElement, useElements } from '@stripe/react-stripe-js';
import { useContext, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { PaymentMethodEnum, ProviderEnum, type ExcludeOtherPaymentMethod } from '@/types/common';
import { isRequireToOpenTab } from '@/utils/redirectPayment';
import { InputTaxComponent } from '../InputTaxComponent/InputTaxComponent';
import { PayButton } from '../PaymentInputs/PayButton';
import { BrandedPayCTA } from '../PaymentInputs/BrandedPayCTA';
import type { StripePaymentResponse } from '@/types/payment';
import { STRIPE_APM_APPEARANCE } from '@/constants/stripe';
import { Email } from '../Common/Email';
import { BizumAuthorizationScreen } from './BizumAuthorizationScreen';
import { MbwayAuthorizationScreen } from './MbwayAuthorizationScreen';
import { shouldHideEmail, shouldShowLoader } from '@/utils/conditionalRendering';
import { useTranslate } from '@/hooks/useTranslate';
import { getLocale } from '@/utils/get-locale-util';
import type { Stripe, StripeElementLocale, StripeError } from '@stripe/stripe-js';
import { stripeService } from '@/services/stripe';
import { Loader } from '../Common/Loader';
import { useStripeInitialize } from '@/hooks/useStripeInitialize';
import { useSelectedPaymentMethod } from '@/hooks/useSelectedPaymentMethod';
import { useBrandedPayCta } from '@/hooks/useBrandedPayCta';
import { isEmailValid } from '@/utils/email';
import { getStripeDeclineReason, getStripePaymentElementDefaultValues } from '@/utils/stripe';
import { getAppchargeErrorMessage } from '@/utils/error';
import PaymentMethodNotSupported from '@/components/PaymentMethod/PaymentMethodNotSupported';
import { logError } from '@/utils/logger';

interface StripeGenericPayContentProps {
  paymentMethod: string;
}

const StripeGenericPayContent = ({ paymentMethod, stripe }: { paymentMethod: string; stripe: Stripe | undefined }) => {
  const elements = useElements();
  const [isValid, setIsValid] = useState(false);
  const [isCustomerAddressAndZipValid, setIsCustomerAddressAndZipValid] = useState(false);
  // Stripe is initialized (Elements provider mounted) well before the inner
  // PaymentElement (card fields / Amazon Pay button / etc.) finishes rendering —
  // and we set `loader: 'never'`, so there's no built-in skeleton covering that
  // gap. Gate the Pay button on Stripe's own `onReady` signal so it can't be
  // clicked against a half-mounted element.
  const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);
  // Stripe fails the element itself (bad key/sub-MID, method not enabled on the
  // account, blocked iframe). `onReady` never fires in that case, so without
  // this the loader overlay below spins forever.
  const [hasLoadError, setHasLoadError] = useState(false);
  const {
    actions: { runPayment, setError, setIsFormValid, openRedirectTab, closeRedirectTab },
    payment: { isInPaymentProcess },
    successScreen,
    paymentMethodsSettings,
    customer,
    featureFlags,
    template,
  } = useContext(CheckoutContext);
  const { t } = useTranslate();
  const { selectedPaymentMethod } = useSelectedPaymentMethod();

  const providerSettings = useMemo(() => {
    return paymentMethodsSettings.find((paymentMethod) => paymentMethod.paymentMethodName === selectedPaymentMethod)!;
  }, [selectedPaymentMethod, paymentMethodsSettings]);

  const paymentElementDefaultValues = useMemo(
    () => getStripePaymentElementDefaultValues(customer, selectedPaymentMethod),
    [customer, selectedPaymentMethod]
  );

  // The PaymentElement re-keys on `providerSettings.id` when the shopper switches
  // methods without this subtree remounting (e.g. two Stripe APMs on the same
  // sub-MID, where `useStripeInitialize` skips its loading phase and the parent
  // doesn't remount). React mounts a fresh element that hasn't fired
  // `onReady`/`onChange` yet, so reset the readiness + completeness flags on the
  // switch — otherwise they'd stay stale-`true` from the previous method, hiding
  // the loader and leaving Pay enabled against a half-mounted element. Skip the
  // initial mount (state already starts `false`; the element sets it via its own
  // `onReady`/`onChange`) so we don't clobber that first signal.
  const previousProviderIdRef = useRef(providerSettings.id);
  useEffect(() => {
    if (previousProviderIdRef.current === providerSettings.id) {
      return;
    }
    previousProviderIdRef.current = providerSettings.id;
    setIsPaymentElementReady(false);
    setIsValid(false);
    setHasLoadError(false);
  }, [providerSettings.id]);

  useEffect(() => {
    setIsFormValid(
      isValid &&
        isEmailValid(customer?.email) &&
        isCustomerAddressAndZipValid &&
        !!stripe &&
        !!elements &&
        isPaymentElementReady
    );
  }, [isValid, stripe, elements, setIsFormValid, isCustomerAddressAndZipValid, isPaymentElementReady, customer?.email]);

  // `isValid` tracks ONLY the Stripe PaymentElement completeness. Email
  // validity is gated separately in `isPayButtonDisabled` (and re-evaluated on
  // every render). Baking the email check in here froze `isValid=false` when
  // the element completed while the email was still empty: the element's
  // `onChange` never re-fires on a later email edit, so the Pay button stayed
  // disabled even after a valid email was entered.
  const handlePaymentElementChange = (event: { complete: boolean }) => {
    setIsValid(event.complete);
  };

  const handlePaymentElementLoadError = (event: { error: StripeError }) => {
    logError('Stripe PaymentElement load error', event?.error);
    setHasLoadError(true);
  };

  const handleAddressAndZipValidate = useCallback((isValid: boolean) => {
    setIsCustomerAddressAndZipValid(isValid);
  }, []);

  const handlePayment = async () => {
    if (!stripe || !elements || !providerSettings) {
      setError(t('paymentMethodNotAvailable'));
      return;
    }

    if (!isEmailValid(customer?.email)) {
      setError(t('fieldErrors.invalidEmail'));
      return;
    }

    // Stripe redirect APMs (Amazon Pay, Klarna, iDEAL, Affirm, mobile CashApp,
    // ...) navigate to the provider in a new tab. The tab must be opened
    // synchronously within this click handler, before the async
    // `elements.submit()` / finalize round-trip — otherwise Safari treats the
    // later `window.open` as a non-gesture popup, blocks it, and the payment
    // gets cancelled. In-page methods (PIX, desktop CashApp/UPI) are excluded by
    // `isRequireToOpenTab`. The opened tab is threaded through context and
    // reused by <RedirectPayment/> via postMessage instead of opening a fresh tab.
    const shouldOpenRedirectTab = isRequireToOpenTab(
      providerSettings.provider as ProviderEnum,
      selectedPaymentMethod as PaymentMethodEnum
    );
    if (shouldOpenRedirectTab) {
      openRedirectTab();
    }

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        if (shouldOpenRedirectTab) {
          closeRedirectTab();
        }
        // `submitError` is Stripe's own Elements validation error (e.g. an
        // incomplete/invalid field) — a real, specific reason, not an unknown
        // failure. Map it the same way a PSP decline is mapped instead of a
        // blanket "something went wrong".
        const declineReason = getStripeDeclineReason(submitError);
        const appChargeErrorType = stripeService.mapStripeErrorCode(declineReason);
        setError(getAppchargeErrorMessage(t, appChargeErrorType));
        return;
      }

      await runPayment<StripePaymentResponse>({ elements, type: paymentMethod });
    } catch {
      if (shouldOpenRedirectTab) {
        closeRedirectTab();
      }
      setError(t('somethingWentWrong'));
    }
  };

  const isPayButtonDisabled =
    !isValid ||
    !isEmailValid(customer?.email) ||
    !isCustomerAddressAndZipValid ||
    !stripe ||
    !elements ||
    !isPaymentElementReady;

  // Templates opting into `preferExpressButton` (PREFERRED today) render an
  // APM-branded CTA for methods that have a config entry — PIX today, since
  // Stripe doesn't ship an ECE for it. Other APMs routed through this same
  // generic form (Klarna, iDEAL, Affirm, CashApp, ...) fall through to the
  // neutral <PayButton> because they're not in BRANDED_PAY_CTA_CONFIG.
  const { shouldRenderBrandedCta } = useBrandedPayCta(selectedPaymentMethod as PaymentMethodEnum | undefined);

  // Bizum's `handleNextAction` resolves in-page while the shopper approves in
  // their banking app, but (unlike a hosted redirect) shows no window of our
  // own. Mirror BLIK's authorization overlay for that wait: it covers the
  // `handleNextAction` window, then hands off to the polling loader once
  // `successScreen` opens (Bizum is in `PaymentMethodsToShowLoaderWhilePolling`).
  const showBizumAuthorizationScreen =
    selectedPaymentMethod === PaymentMethodEnum.BIZUM &&
    isInPaymentProcess &&
    !successScreen?.isOpen &&
    shouldShowLoader(featureFlags);

  // MB WAY mirrors Bizum: the shopper approves in the MB WAY app while
  // `handleNextAction` resolves in-page (no window of our own), so cover that
  // wait with an authorization overlay, then hand off to the polling loader once
  // `successScreen` opens (MB WAY is in `PaymentMethodsToShowLoaderWhilePolling`).
  const showMbwayAuthorizationScreen =
    selectedPaymentMethod === PaymentMethodEnum.MBWAY &&
    isInPaymentProcess &&
    !successScreen?.isOpen &&
    shouldShowLoader(featureFlags);

  if (hasLoadError) {
    return <PaymentMethodNotSupported />;
  }

  return (
    <div className="flex flex-col gap-4">
      {showBizumAuthorizationScreen && <BizumAuthorizationScreen />}
      {showMbwayAuthorizationScreen && <MbwayAuthorizationScreen />}
      {!shouldHideEmail(featureFlags, customer, template.features) && <Email />}
      <InputTaxComponent onValidate={handleAddressAndZipValidate} />
      {/* With `loader: 'never'` on <Elements/> there's no built-in skeleton for
          the gap between mount and `onReady`. Overlay our own spinner until the
          element is ready so the area never reads as blank — most visible for
          field-less APMs like Bizum, whose PaymentElement renders nothing. The
          element stays mounted underneath (not display:none) so `onReady` fires
          reliably; the min-height only applies while loading so a ready empty
          element (Bizum) collapses and card fields size themselves. */}
      <div className={`relative ${isPaymentElementReady ? '' : 'min-h-[44px]'}`}>
        {!isPaymentElementReady && (
          <div
            data-testid="stripe-payment-element-loader"
            className="absolute inset-0 z-10 flex items-center justify-center"
          >
            <Loader size="xs" variant="checkout" />
          </div>
        )}
        <PaymentElement
          key={providerSettings.id}
          id={`${paymentMethod}-payment-element`}
          onChange={handlePaymentElementChange}
          onReady={() => setIsPaymentElementReady(true)}
          onLoadError={handlePaymentElementLoadError}
          options={{
            layout: 'auto',
            fields: {
              billingDetails: {
                email: 'never',
              },
            },
            defaultValues: paymentElementDefaultValues,
          }}
        />
      </div>
      {shouldRenderBrandedCta && selectedPaymentMethod ? (
        <BrandedPayCTA
          paymentMethod={selectedPaymentMethod as PaymentMethodEnum}
          onClick={handlePayment}
          isDisabled={isPayButtonDisabled}
        />
      ) : (
        <PayButton onClick={handlePayment} isDisabled={isPayButtonDisabled} />
      )}
    </div>
  );
};

export const StripeGenericPayForm = ({ paymentMethod }: StripeGenericPayContentProps) => {
  const { pricing, paymentMethodSelected, otherMethodSelected } = useContext(CheckoutContext);
  const { pspLocale } = getLocale();
  const selectedPaymentMethod =
    paymentMethodSelected === PaymentMethodEnum.OTHER ? otherMethodSelected : paymentMethodSelected;
  const { stripe, isLoading: isStripeLoading } = useStripeInitialize({
    paymentMethod: selectedPaymentMethod as ExcludeOtherPaymentMethod,
  });

  if (isStripeLoading || !stripe) {
    if (!isStripeLoading && !stripe) {
      return <PaymentMethodNotSupported />;
    }
    return <Loader />;
  }
  return (
    <div className="flex flex-col gap-4">
      <Elements
        stripe={stripe}
        options={{
          mode: 'payment',
          amount: Math.round(pricing.priceBreakdown.priceTotal),
          currency: pricing.currencyCode.toLowerCase(),
          paymentMethodTypes: [paymentMethod],
          paymentMethodCreation: 'manual',
          appearance: STRIPE_APM_APPEARANCE,
          locale: pspLocale as StripeElementLocale,
          loader: 'never',
        }}
      >
        <StripeGenericPayContent paymentMethod={paymentMethod} stripe={stripeService.getStripe()} />
      </Elements>
    </div>
  );
};
